import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import ConceptActions from "../components/ConceptActions";
import Comments from "../components/Comments";
import WhoPinnedThis from "../components/WhoPinnedThis";

export default function ConceptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [concept, setConcept] = useState(null);
  const [correlations, setCorrelations] = useState([]);
  const [thread, setThread] = useState([]);

  useEffect(() => {
    api.get(`/concepts/${id}`).then(({ data }) => setConcept(data));
    api.get("/correlations", { params: { concept_id: id } }).then(({ data }) => setCorrelations(data));
    api.get(`/concepts/${id}/thread`).then(({ data }) => setThread(data)).catch((err) => console.error("thread fetch failed", err));
    // Track read (server-side for logged-in users; localStorage for everyone — used to trigger the soft banner).
    api.post(`/users/me/read/${id}`).catch(() => {});
    try {
      const reads = new Set(JSON.parse(localStorage.getItem("cor_reads") || "[]"));
      reads.add(id);
      localStorage.setItem("cor_reads", JSON.stringify([...reads]));
    } catch (e) { console.warn("read tracking failed", e); }
  }, [id]);

  if (!concept) {
    return <div className="max-w-3xl mx-auto px-6 py-32 text-center text-muted-foreground/75 italic serif text-xl">Unsealing…</div>;
  }

  return (
    <div data-testid="concept-detail" className="max-w-5xl mx-auto px-6 sm:px-10 py-20 reveal">
      <Link to="/archive" className="label link-underline" data-testid="back-to-archive">← Back to Archive</Link>

      <div className="mt-10 flex items-center gap-3">
        <span className={`tag tag-${concept.source_type}`}>{concept.source_type}</span>
        <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/75">
          {concept.traditions?.length} traditions
        </span>
      </div>

      <h1 className="mt-6 serif text-5xl sm:text-7xl text-foreground leading-[1.02] tracking-tight">
        {concept.title}
      </h1>
      <p className="mt-8 text-xl text-foreground/75 max-w-3xl leading-relaxed serif italic font-light">
        {concept.summary}
      </p>

      <div className="mt-10">
        <ConceptActions conceptId={concept.id} conceptTitle={concept.title} />
      </div>

      {/* SIDE-BY-SIDE TRADITIONS */}
      <section className="mt-20">
        <div className="label mb-8">Parallels</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-border">
          {concept.traditions?.map((t, i) => (
            <div key={i} className="bg-card p-8" data-testid={`tradition-${i}`}>
              <div className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--primary))] mb-3">
                {t.tradition}
              </div>
              <div className="serif text-3xl text-foreground mb-3">{t.term}</div>
              <p className="text-muted-foreground leading-relaxed text-[15px]">{t.note}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ESSAY */}
      {concept.essay && (
        <section className="mt-24 max-w-3xl">
          <div className="label mb-6">Convergence</div>
          <p className="serif text-2xl text-foreground leading-relaxed font-light">
            {concept.essay}
          </p>
        </section>
      )}

      {/* RELATED CORRELATIONS */}
      {correlations.length > 0 && (
        <section className="mt-24">
          <div className="label mb-6">Related correlations</div>
          <ul className="space-y-6">
            {correlations.map((c) => (
              <li key={c.id} className="hairline-b pb-6">
                <div className="flex items-center gap-3 mb-2">
                  <span className="mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground/75">{c.strength}</span>
                </div>
                <div className="serif text-2xl text-foreground">{c.title}</div>
                <p className="mt-2 text-muted-foreground">{c.summary}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* FOLLOW THE THREAD — rabbit-hole flow */}
      {thread.length > 0 && (
        <section className="mt-24" data-testid="follow-the-thread">
          <div className="label mb-3 text-[hsl(var(--accent))]">Follow the thread</div>
          <p className="serif italic text-muted-foreground mb-8 max-w-xl">
            Wherever you begin, you arrive somewhere. Pull on one of these and see where it leads —
            curiosity is the only compass that always works.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-border">
            {thread.map((c) => (
              <Link
                key={c.id}
                to={`/concept/${c.id}`}
                data-testid={`thread-${c.id}`}
                className="bg-card p-6 hover:bg-secondary transition-colors duration-500 group block"
              >
                <span className={`tag tag-${c.source_type}`}>{c.source_type}</span>
                <h4 className="serif text-xl text-foreground mt-3 group-hover:text-[hsl(var(--accent))] transition-colors">{c.title}</h4>
                <p className="mt-2 text-muted-foreground text-[13px] leading-relaxed line-clamp-2">{c.summary}</p>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mt-28 hairline-t pt-12 flex flex-wrap items-center justify-between gap-6">
        <p className="serif italic text-muted-foreground text-lg max-w-md">
          You are not the first to feel this. You will not be the last. Add what you have seen.
        </p>
        <button
          type="button"
          className="btn-gold"
          data-testid="cta-submit-from-concept"
          onClick={() => {
            if (!user) { navigate("/login"); return; }
            window.dispatchEvent(new CustomEvent("cor:open-compose", {
              detail: {
                title: `A correlation for ${concept.title}`,
                body: `I saw this in ${concept.title}:\n\n`,
              },
            }));
          }}
        >
          Contribute a correlation
        </button>
      </section>

      <WhoPinnedThis conceptId={concept.id} />

      <Comments conceptId={concept.id} />
    </div>
  );
}
